import React, { useEffect, useState } from 'react'
import { Avatar, Button, Input, Upload, UploadFile, UploadProps, message } from 'antd'

import editBlue from 'assets/images/edit-blue.png';
import alertTriangle from 'assets/images/alert-triangle.svg';
import { useGetUserLoggin } from '../../../../services/user/querys';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useUpdateUser } from '../../../../services/user/mutations';
import InputBorderNone from '../../../../components/Atoms/Inputs/InputBorderNone/InputBorderNone';
import { formatPhoneNumber } from '../../../../@common/utils/formatters/formatter';
import ModalBasic from '../../../../components/Organisms/Modals/ModalBasic/ModalBasic';

const CardProfile = () => {

  const { data: user, fetching: loadingUser } = useGetUserLoggin();
  const { execute: updateUser, fetching: loadingUpdate } = useUpdateUser();
  
  const [isUpdatingToken, setIsUpdatingToken] = useState(false);
  
  const [fileAvatar, setFileAvatar] = useState<UploadFile>();
  const [cachedUser, setCachedUser] = useState<any | null>(null);
  const [isEdit, setEdit] = useState(false);
  const [previewAvatar, setPreviewAvatar] = useState();
  const [editName, setEditName] = useState();
  const [editLastname, setEditLastname] = useState();
  const [editEmail, setEditEmail] = useState();
  const [editPhone, setEditPhone] = useState();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState<string | null>(null);

  const handlePhoneChange = (e: any) => {
    const value = e.target.value;
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 10) {
      setEditPhone(cleaned);
    }
  };

  useEffect(() => {
    if (user) 
      setCachedUser(user);
  }, [user]);

  const displayUser = cachedUser || user;

  useEffect(() => {
    if (!fileAvatar) {
      setPreviewAvatar(undefined)
      return
    }

    const objectUrl = URL.createObjectURL(fileAvatar as any)
    setPreviewAvatar(objectUrl as any)

    return () => URL.revokeObjectURL(objectUrl)
  }, [fileAvatar])

  const onClickEdit = async () => {
    if (isEdit) {
      const phoneToSave = editPhone ? formatPhoneNumber(editPhone) : displayUser?.phone;
      const newEmail = editEmail || displayUser?.email;
      
      if (newEmail && newEmail !== user?.email) {
        setPendingEmailChange(newEmail);
        setShowEmailChangeModal(true);
        return;
      }
      
      if (newEmail) {
        await performUserUpdate(newEmail, phoneToSave);
      }
    } else {
      setErrorMessage(null);
      setEdit(true);
    }
  }

  const performUserUpdate = async (email: string, phone?: string | null) => {
    let payload: any = {
      _id: displayUser?._id, 
      name: editName || displayUser?.name,
      lastName: editLastname || displayUser?.lastName,
      email: email,
      phone: phone,
    };

    if(fileAvatar)
      payload.avatar = fileAvatar;
      
    try {
      setErrorMessage(null);
      setIsUpdatingToken(true);
      
      const result = await updateUser(payload as any);
      
      if (result.error?.graphQLErrors?.[0]?.message === 'Email is already in use by another user') {
        setErrorMessage('Email is already in use by another user');
        setIsUpdatingToken(false);
        return;
      }
            
      setPreviewAvatar(undefined);
      setFileAvatar(undefined);
      setEdit(false);
      setIsUpdatingToken(false);
    } catch (error) {
      setIsUpdatingToken(false);
    }
  }

  const handleEmailChangeConfirm = async () => {
    setShowEmailChangeModal(false);
    const phoneToSave = editPhone ? formatPhoneNumber(editPhone) : user?.phone;
    if (pendingEmailChange) {
      await performUserUpdate(pendingEmailChange, phoneToSave);
    }
    setPendingEmailChange(null);
  }

  const handleEmailChangeCancel = () => {
    setShowEmailChangeModal(false);
    setPendingEmailChange(null);
  }

  const props: UploadProps = {
    beforeUpload: (file) => {
      setFileAvatar(file);
      return false;
    },
    maxCount: 1,
    showUploadList: false,
  };

  const uploadButton = (
    previewAvatar ?
      <Avatar src={previewAvatar} size={68} className='avatar' />
      :
      <button style={{ border: 0, background: 'none' }} type="button">
        <PlusOutlined />
      </button>
  );

  return (
    <div className='card-profile-generic flex-btw gap-3'>
      <div className='flex-btw gap-3'>

        {!isEdit &&
          <Avatar src={!loadingUser && !loadingUpdate && !isUpdatingToken && user?.avatar?.url} size={68} className='avatar'>
            {!user?.avatar && user?.name?.charAt(0)}
            {(loadingUser || loadingUpdate || isUpdatingToken) && <LoadingOutlined />}
          </Avatar>
        }

        {isEdit &&
          <Upload
            name="avatar"
            listType="picture-circle"
            className="avatar"
            {...props}
          >
            {uploadButton}
          </Upload>
        }
        <div className='body'>
          {!isEdit &&
            <>
              <h1 className='card-title'>{displayUser?.name + ' ' + displayUser?.lastName}</h1>
              <h5 className='title-normal mt-5'>{displayUser?.email}</h5>
              <h5 className='title-normal mt-5'>{displayUser?.phone ? formatPhoneNumber(displayUser.phone) : displayUser?.phone}</h5>
            </>
          }
          {isEdit &&
            <div>
              <InputBorderNone
                color="bold-lith"
                placeholder="Name"
                onChange={(e: any) => setEditName(e.target.value)}
                defaultValue={displayUser?.name}
              />
              <InputBorderNone
                color="bold-lith"
                placeholder="Lastname"
                onChange={(e: any) => setEditLastname(e.target.value)}
                defaultValue={displayUser?.lastName}
                className="mt-5"
              />
              <Input.TextArea
                className="input input-transparent body-placeholder mt-5 bold-lith"
                placeholder="Email"
                onChange={(e: any) => setEditEmail(e.target.value)}
                defaultValue={displayUser?.email}
                autoSize={{ minRows: 1 }}
                style={{ resize: 'none' }}
              />
              <InputBorderNone
                color="bold-lith"
                placeholder="Phone"
                onChange={handlePhoneChange}
                value={editPhone !== undefined ? formatPhoneNumber(editPhone) : (displayUser?.phone ? formatPhoneNumber(displayUser.phone) : '')}
                maxLength={14}
                onKeyDown={(e: any) => {
                  // Only allow numbers, backspace, delete, and arrow keys
                  if (!/[0-9]/.test(e.key) && 
                      e.key !== 'Backspace' && 
                      e.key !== 'Delete' && 
                      e.key !== 'ArrowLeft' && 
                      e.key !== 'ArrowRight' && 
                      e.key !== 'Tab') {
                    e.preventDefault();
                  }
                }}
                className="mt-5"
              />
            </div>
          }
          {errorMessage && (
            <div className="error-message mt-10" style={{ 
              color: '#ff4d4f', 
              fontSize: '14px', 
              padding: '8px 12px', 
              backgroundColor: '#fff2f0', 
              border: '1px solid #ffccc7', 
              borderRadius: '6px',
              marginTop: '10px'
            }}>
              {errorMessage}
            </div>
          )}
          <Button type="text" className='button button-round mt-15'>{user?.__typename?.toUpperCase()}</Button>
        </div>
      </div>
      <Button className='button button-transparent button-edit' onClick={onClickEdit}>
        <img src={editBlue} className='edit' alt='edit' />
        {isEdit ? 'SAVE' : 'EDIT'}
      </Button>
      
      <ModalBasic
        visible={showEmailChangeModal}
        setVisible={setShowEmailChangeModal}
        title="Email Change"
        description="By changing your email, your session will be closed automatically and you will need to log in again with your new email."
        icon={alertTriangle}
        className="modal-email-change-confirmation"
        submit={{
          buttonLabel: "Continue",
          onSubmit: handleEmailChangeConfirm,
          color: "red"
        }}
        cancel={{
          buttonLabel: "Cancel",
          color: "white"
        }}
      />
    </div>
  )
}

export default CardProfile